/**
 * Utility for computing word-level string differences
 * Used for highlighting changes in version comparison
 */

/**
 * Compute the Longest Common Subsequence (LCS) length matrix
 * @param {string[]} arr1 - First array of words
 * @param {string[]} arr2 - Second array of words
 * @returns {number[][]} LCS length matrix
 */
const computeLCSMatrix = (arr1, arr2) => {
  const m = arr1.length;
  const n = arr2.length;
  const dp = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (arr1[i - 1] === arr2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  return dp;
};

/**
 * Backtrack through LCS matrix to find common words
 * @param {number[][]} dp - LCS length matrix
 * @param {string[]} arr1 - First array of words
 * @param {string[]} arr2 - Second array of words
 * @param {number} i - Current index in arr1
 * @param {number} j - Current index in arr2
 * @returns {string[]} Array of common words
 */
const backtrackLCS = (dp, arr1, arr2, i, j) => {
  if (i === 0 || j === 0) {
    return [];
  }

  if (arr1[i - 1] === arr2[j - 1]) {
    return [...backtrackLCS(dp, arr1, arr2, i - 1, j - 1), arr1[i - 1]];
  }

  if (dp[i - 1][j] > dp[i][j - 1]) {
    return backtrackLCS(dp, arr1, arr2, i - 1, j);
  }

  return backtrackLCS(dp, arr1, arr2, i, j - 1);
};

/**
 * Compute word-level diff between two strings
 * @param {string} oldStr - Original string
 * @param {string} newStr - Modified string
 * @returns {Array<{type: string, value: string}>} Array of diff segments
 */
const computeWordDiff = (oldStr, newStr) => {
  if (oldStr === null || oldStr === undefined) oldStr = "";
  if (newStr === null || newStr === undefined) newStr = "";

  oldStr = String(oldStr);
  newStr = String(newStr);

  if (oldStr === newStr) {
    return [{ type: "unchanged", value: oldStr }];
  }

  if (oldStr === "") {
    return [{ type: "added", value: newStr }];
  }

  if (newStr === "") {
    return [{ type: "removed", value: oldStr }];
  }

  const oldWords = oldStr.split(/(\s+)/);
  const newWords = newStr.split(/(\s+)/);

  const lcsMatrix = computeLCSMatrix(oldWords, newWords);
  const commonWords = backtrackLCS(lcsMatrix, oldWords, newWords, oldWords.length, newWords.length);

  const diff = [];
  let oldIdx = 0;
  let newIdx = 0;
  let commonIdx = 0;

  while (oldIdx < oldWords.length || newIdx < newWords.length) {
    if (commonIdx < commonWords.length && oldIdx < oldWords.length && oldWords[oldIdx] === commonWords[commonIdx]) {
      if (newIdx < newWords.length && newWords[newIdx] === commonWords[commonIdx]) {
        diff.push({ type: "unchanged", value: oldWords[oldIdx] });
        oldIdx++;
        newIdx++;
        commonIdx++;
      } else {
        diff.push({ type: "added", value: newWords[newIdx] });
        newIdx++;
      }
    } else if (commonIdx < commonWords.length && newIdx < newWords.length && newWords[newIdx] === commonWords[commonIdx]) {
      diff.push({ type: "removed", value: oldWords[oldIdx] });
      oldIdx++;
    } else if (oldIdx < oldWords.length && newIdx < newWords.length) {
      if (oldWords[oldIdx].trim() && newWords[newIdx].trim()) {
        diff.push({ type: "removed", value: oldWords[oldIdx] });
        diff.push({ type: "added", value: newWords[newIdx] });
      } else if (oldWords[oldIdx].trim()) {
        diff.push({ type: "removed", value: oldWords[oldIdx] });
      } else if (newWords[newIdx].trim()) {
        diff.push({ type: "added", value: newWords[newIdx] });
      } else {
        if (oldWords[oldIdx] === newWords[newIdx]) {
          diff.push({ type: "unchanged", value: oldWords[oldIdx] });
        } else {
          diff.push({ type: "removed", value: oldWords[oldIdx] });
          diff.push({ type: "added", value: newWords[newIdx] });
        }
      }
      oldIdx++;
      newIdx++;
    } else if (oldIdx < oldWords.length) {
      diff.push({ type: "removed", value: oldWords[oldIdx] });
      oldIdx++;
    } else if (newIdx < newWords.length) {
      diff.push({ type: "added", value: newWords[newIdx] });
      newIdx++;
    }
  }

  return mergeConsecutiveSegments(diff);
};

/**
 * Merge consecutive segments of the same type
 * @param {Array} segments - Array of diff segments
 * @returns {Array} Merged segments
 */
const mergeConsecutiveSegments = (segments) => {
  if (segments.length === 0) return segments;

  const merged = [segments[0]];

  for (let i = 1; i < segments.length; i++) {
    const current = segments[i];
    const last = merged[merged.length - 1];

    if (last.type === current.type) {
      last.value += current.value;
    } else {
      merged.push(current);
    }
  }

  return merged;
};

/**
 * Check if a string value should be diffed word-by-word
 * @param {any} value - Value to check
 * @returns {boolean} True if value should be diffed
 */
const shouldDiffAsString = (value) => {
  if (typeof value !== "string") return false;

  if (value.startsWith("{") || value.startsWith("[")) {
    try {
      JSON.parse(value);
      return false;
    } catch (e) {
      // Not JSON, treat as string
    }
  }

  return value.trim().length > 0;
};

export default {
  computeWordDiff,
  shouldDiffAsString,
};